import { useState, useCallback, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearch } from '@embedpdf/plugin-search/react'
import { useAnnotation } from '@embedpdf/plugin-annotation/react'
import { useScroll } from '@embedpdf/plugin-scroll/react'
import { DictionaryService } from '../../services/dictionary-service'
import { useDocumentManagerCapability } from '@embedpdf/plugin-document-manager/react'
import { PdfAnnotationSubtype, MatchFlag } from '@embedpdf/models'
import { Icon } from '../ui/icon'
import type { RightPanelMode } from './SimplePdfViewer'
import { OcrService } from '../../services/ocr/ocr-service'
import { useResponsive } from '../../hooks/useResponsive'

interface RightPanelProps {
  isOpen: boolean
  onClose: () => void
  documentId: string
  mode: RightPanelMode
}

function SearchPanel({ documentId, isVisible }: { documentId: string; isVisible: boolean }) {
  const { t } = useTranslation()
  const { state, provides: searchScope } = useSearch(documentId)
  const [query, setQuery] = useState('')
  const [caseSensitive, setCaseSensitive] = useState(false)
  const [wholeWord, setWholeWord] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout>>()

  useEffect(() => {
    if (isVisible) {
      inputRef.current?.focus()
    }
  }, [isVisible])

  useEffect(() => {
    const flags: MatchFlag[] = []
    if (caseSensitive) flags.push(MatchFlag.MatchCase)
    if (wholeWord) flags.push(MatchFlag.MatchWholeWord)
    searchScope?.setFlags(flags)
  }, [caseSensitive, wholeWord, searchScope])

  useEffect(() => {
    if (!query.trim()) {
      searchScope?.stopSearch()
      return
    }
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      searchScope?.stopSearch()
      searchScope?.searchAllPages(query.trim())
    }, 400)
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [query, searchScope])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault()
        if (debounceRef.current) {
          clearTimeout(debounceRef.current)
          searchScope?.stopSearch()
          searchScope?.searchAllPages(query.trim())
        }
        if (e.shiftKey) {
          searchScope?.previousResult()
        } else {
          searchScope?.nextResult()
        }
      }
    },
    [query, searchScope],
  )

  const handleStop = useCallback(() => {
    searchScope?.stopSearch()
    if (debounceRef.current) clearTimeout(debounceRef.current)
  }, [searchScope])

  return (
    <div className="flex flex-col h-full">
      <div className="p-3 space-y-3 border-b border-border-default">
        <div className="relative">
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t('style.searchPlaceholder') || 'Search document...'}
            spellCheck={false}
            autoComplete="off"
            className="w-full h-9 bg-bg-elevated text-fg-primary text-sm rounded-lg border border-border-default pl-9 pr-16 outline-none focus:border-accent placeholder:text-fg-muted appearance-none"
            style={{ WebkitTextFillColor: 'white', caretColor: 'var(--color-accent, #3B82F6)' }}
          />
          <Icon icon="search" size={16} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-fg-muted" />
          <div className="absolute right-1 top-1/2 -translate-y-1/2 flex items-center gap-0.5">
            {state.loading ? (
              <button
                onClick={handleStop}
                className="h-7 px-2 rounded text-xs text-fg-secondary hover:text-fg-primary hover:bg-bg-surface-alt transition-colors"
                title={t('style.searchStop') || 'Stop search'}
              >
                <Icon icon="x" size={14} />
              </button>
            ) : null}
          </div>
        </div>

        <div className="flex items-center gap-3">
          <label className="flex items-center gap-1.5 cursor-pointer select-none">
            <div className={`w-4 h-4 rounded border flex items-center justify-center transition-colors ${caseSensitive ? 'bg-accent border-accent' : 'border-border-strong bg-bg-input'}`}>
              {caseSensitive && <Icon icon="check" size={10} primaryColor="#FFFFFF" />}
            </div>
            <input
              type="checkbox"
              checked={caseSensitive}
              onChange={(e) => setCaseSensitive(e.target.checked)}
              className="hidden"
            />
            <span className="text-xs text-fg-secondary">{t('style.searchCaseSensitive') || 'Aa'}</span>
          </label>
          <label className="flex items-center gap-1.5 cursor-pointer select-none">
            <div className={`w-4 h-4 rounded border flex items-center justify-center transition-colors ${wholeWord ? 'bg-accent border-accent' : 'border-border-strong bg-bg-input'}`}>
              {wholeWord && <Icon icon="check" size={10} primaryColor="#FFFFFF" />}
            </div>
            <input
              type="checkbox"
              checked={wholeWord}
              onChange={(e) => setWholeWord(e.target.checked)}
              className="hidden"
            />
            <span className="text-xs text-fg-secondary">{t('style.searchWholeWord') || 'Ab'}</span>
          </label>
        </div>

        {state.total > 0 && (
          <div className="flex items-center justify-between">
            <span className="text-xs text-fg-muted">
              {t('style.searchResults')?.replace('{current}', String(state.activeResultIndex + 1)).replace('{total}', String(state.total)) ||
                `${state.activeResultIndex + 1} of ${state.total}`}
            </span>
            <div className="flex items-center gap-0.5">
              <button
                onClick={() => searchScope?.previousResult()}
                className="h-7 w-7 flex items-center justify-center rounded text-fg-secondary hover:text-fg-primary hover:bg-bg-surface-alt transition-colors"
                title={t('style.searchPrev') || 'Previous result (Shift+Enter)'}
              >
                <Icon icon="chevronLeft" size={16} />
              </button>
              <button
                onClick={() => searchScope?.nextResult()}
                className="h-7 w-7 flex items-center justify-center rounded text-fg-secondary hover:text-fg-primary hover:bg-bg-surface-alt transition-colors"
                title={t('style.searchNext') || 'Next result (Enter)'}
              >
                <Icon icon="chevronRight" size={16} />
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="flex-1 overflow-y-auto p-3">
        {state.loading && (
          <div className="flex items-center gap-2 text-fg-muted text-xs py-4 justify-center">
            <div className="w-3 h-3 border-2 border-accent border-t-transparent rounded-full animate-spin" />
            {t('style.searching') || 'Searching...'}
          </div>
        )}

        {!state.loading && state.results.length === 0 && query.trim() && (
          <div className="py-8 text-center">
            <span className="text-fg-muted text-xs">{t('style.searchNoResults') || 'No results found'}</span>
          </div>
        )}

        {!state.loading && state.results.length === 0 && !query.trim() && (
          <div className="py-12 text-center">
            <p className="text-fg-muted text-xs">{t('style.searchStartTyping') || 'Type a query to search the document'}</p>
          </div>
        )}

        {state.results.length > 0 && (
          <div className="space-y-1">
            {state.results.map((result, index) => (
              <button
                key={index}
                onClick={() => searchScope?.goToResult(index)}
                className={`w-full text-left p-2 rounded-lg text-xs transition-colors ${
                  index === state.activeResultIndex
                    ? 'bg-accent/10 text-accent border border-accent/20'
                    : 'text-fg-secondary hover:bg-bg-surface-alt border border-transparent'
                }`}
              >
                <div className="flex items-center gap-2">
                  <span className="text-fg-muted font-mono flex-shrink-0">p.{result.pageIndex + 1}</span>
                  <span className="truncate">
                    <span className="text-fg-muted">{result.context.before}</span>
                    <span className="font-semibold text-accent">{result.context.match}</span>
                    <span className="text-fg-muted">{result.context.after}</span>
                  </span>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

function CommentsPanel({ documentId }: { documentId: string }) {
  const { t } = useTranslation()
  const { state: annotationState, provides: annotationApi } = useAnnotation(documentId)
  const { provides: scrollApi } = useScroll(documentId)
  const { provides: docManager } = useDocumentManagerCapability()
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editText, setEditText] = useState('')
  const autoEditRef = useRef(false)

  const comments = Object.values(annotationState?.byUid ?? {})
    .filter((a: any) => a.object?.type === PdfAnnotationSubtype.TEXT || a.object?.type === 'text')
    .sort((a: any, b: any) => (a.object?.pageIndex ?? 0) - (b.object?.pageIndex ?? 0) || (a.object?.created ?? 0) - (b.object?.created ?? 0))

  useEffect(() => {
    if (autoEditRef.current || !annotationApi) return
    const empty = comments.find((c: any) => !c.object?.contents)
    if (empty) {
      const id = empty.object?.id
      if (editingId !== id) {
        setEditingId(id)
        setEditText('')
        autoEditRef.current = true
      }
    }
  }, [comments])

  useEffect(() => {
    if (!editingId) {
      autoEditRef.current = false
    }
  }, [editingId])

  const handleNavigate = useCallback(
    (pageIndex: number) => {
      scrollApi?.scrollToPage({ pageNumber: pageIndex + 1, behavior: 'auto' })
    },
    [scrollApi],
  )

  const handleEdit = useCallback((comment: any) => {
    setEditingId(comment.object?.id ?? comment.id)
    setEditText(comment.object?.contents || '')
  }, [])

  const handleSaveEdit = useCallback(() => {
    if (!editingId || !annotationApi || !editText.trim()) return
    const comment = comments.find((c: any) => (c.object?.id ?? c.id) === editingId)
    if (!comment) return
    annotationApi.updateAnnotation(comment.object?.pageIndex ?? 0, editingId, { contents: editText.trim() })
    setEditingId(null)
    setEditText('')
  }, [editingId, editText, annotationApi, comments])

  const handleCancelEdit = useCallback(() => {
    if (!editingId || !annotationApi) return
    const comment = comments.find((c: any) => (c.object?.id ?? c.id) === editingId)
    if (comment && !comment.object?.contents) {
      annotationApi.deleteAnnotation(comment.object?.pageIndex ?? 0, editingId)
    }
    setEditingId(null)
    setEditText('')
  }, [editingId, annotationApi, comments])

  const handleDelete = useCallback(
    (comment: any) => {
      if (!annotationApi) return
      annotationApi.deleteAnnotation(comment.object?.pageIndex ?? 0, comment.object?.id ?? comment.id)
    },
    [annotationApi],
  )

  const handleClearAll = useCallback(() => {
    if (!annotationApi) return
    if (!confirm(t('style.clearAllConfirm') || 'Delete all comments?')) return
    for (const comment of comments) {
      annotationApi.deleteAnnotation(comment.object?.pageIndex ?? 0, comment.object?.id ?? comment.id)
    }
  }, [annotationApi, comments, t])

  const handleExport = useCallback(() => {
    const docState = docManager?.getDocumentState(documentId)
    const docName = docState?.name || docState?.id || 'document'
    const safeName = docName.replace(/[^a-zA-Z0-9\-_\u1780-\u17FF\u0E00-\u0E7F]/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '')
    const lines: string[] = []
    lines.push(`Comments for ${docName}`)
    lines.push('='.repeat(50))
    lines.push('')

    let currentPage = -1
    for (const c of comments) {
      const obj = c.object
      if (obj?.pageIndex !== currentPage) {
        currentPage = obj?.pageIndex ?? 0
        lines.push(`Page ${currentPage + 1}`)
        lines.push('-'.repeat(30))
      }
      const text = obj?.contents || '(empty)'
      const date = obj?.modified ? new Date(obj.modified).toLocaleDateString() : ''
      lines.push(`  [${date}] ${text}`)
      lines.push('')
    }

    const blob = new Blob([lines.join('\n')], { type: 'text/plain;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${safeName}_comments.txt`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    setTimeout(() => URL.revokeObjectURL(url), 1000)
  }, [comments, docManager, documentId])

  if (comments.length === 0) {
    return (
      <div className="flex-1 p-8 flex flex-col items-center justify-center gap-4 text-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-3xl bg-bg-app text-fg-muted">
          <Icon icon="comment" size={24} />
        </div>
        <div className="max-w-[220px]">
          <p className="text-fg-primary font-semibold mb-2">{t('style.noComments') || 'No comments yet'}</p>
          <p className="text-fg-muted text-sm">{t('style.noCommentsHint') || 'Use the Comment tool to add notes to the document.'}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      <div className="flex items-center justify-between px-3 py-2 border-b border-border-default bg-bg-app">
        <span className="text-xs text-fg-muted">
          {comments.length} {comments.length === 1 ? (t('style.comment') || 'comment') : (t('style.comments') || 'comments')}
        </span>
        <div className="flex items-center gap-1">
          <button
            onClick={handleExport}
            className="flex items-center gap-1 h-7 px-2 rounded text-xs text-fg-secondary hover:text-fg-primary hover:bg-bg-surface-alt transition-colors"
            title={t('style.exportComments') || 'Export comments'}
          >
            <Icon icon="download" size={12} />
            {t('style.export') || 'Export'}
          </button>
          <button
            onClick={handleClearAll}
            className="flex items-center gap-1 h-7 px-2 rounded text-xs text-red-400 hover:text-red-300 hover:bg-red-500/10 transition-colors"
            title={t('style.clearAll') || 'Clear All'}
          >
            <Icon icon="trash" size={12} />
            {t('style.clearAll') || 'Clear'}
          </button>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-3 space-y-1">
      {comments.map((comment: any) => {
        const obj = comment.object
        const id = obj?.id ?? ''
        const pageIndex = obj?.pageIndex ?? 0
        const contents = obj?.contents || ''
        const modified = obj?.modified
        return (
        <div
          key={id}
          className={`p-2 rounded-lg border transition-colors ${
            editingId === id ? 'border-accent bg-accent/5' : 'border-border-default hover:bg-bg-surface-alt'
          }`}
        >
          <div className="flex items-start gap-2">
            <button
              onClick={() => handleNavigate(pageIndex)}
              className="flex-shrink-0 text-xs font-mono text-fg-muted hover:text-accent transition-colors mt-0.5"
            >
              p.{pageIndex + 1}
            </button>

            {editingId === id ? (
              <div className="flex-1 flex flex-col gap-1">
                <textarea
                  value={editText}
                  onChange={(e) => setEditText(e.target.value)}
                  placeholder={t('style.commentPlaceholder') || 'Write a comment...'}
                  className="w-full min-h-[60px] bg-bg-elevated text-fg-primary text-xs rounded border border-border-default p-2 outline-none focus:border-accent resize-y placeholder:text-fg-muted/50"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') {
                      handleCancelEdit()
                    }
                    if (e.key === 'Enter' && !e.shiftKey && editText.trim()) {
                      e.preventDefault()
                      handleSaveEdit()
                    }
                  }}
                />
                <div className="flex items-center gap-1">
                  <button
                    onClick={handleSaveEdit}
                    disabled={!editText.trim()}
                    className={`h-7 w-7 flex items-center justify-center rounded transition-colors ${
                      editText.trim()
                        ? 'text-green-500 hover:bg-green-500/20 cursor-pointer'
                        : 'text-fg-muted/30 cursor-not-allowed'
                    }`}
                    title={t('style.commitComment') || 'Save comment'}
                  >
                    <Icon icon="check" size={16} />
                  </button>
                  <button
                    onClick={handleCancelEdit}
                    className="h-7 w-7 flex items-center justify-center rounded text-fg-muted hover:text-red-500 hover:bg-red-500/10 transition-colors cursor-pointer"
                    title={t('style.discardComment') || 'Discard'}
                  >
                    <Icon icon="trash" size={14} />
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex-1 min-w-0">
                <p className="text-xs text-fg-primary leading-relaxed whitespace-pre-wrap break-words">
                  {contents || '(empty)'}
                </p>
                {modified && (
                  <span className="text-[10px] text-fg-muted">
                    {new Date(modified).toLocaleDateString()}
                  </span>
                )}
              </div>
            )}

            {editingId !== id && (
              <div className="flex-shrink-0 flex items-center gap-0.5">
                <button
                  onClick={() => handleEdit(comment)}
                  className="h-6 w-6 flex items-center justify-center rounded text-fg-muted hover:text-fg-primary hover:bg-bg-surface-alt transition-colors"
                  title={t('style.edit') || 'Edit'}
                >
                  <Icon icon="pencilMarker" size={12} />
                </button>
                <button
                  onClick={() => handleDelete(comment)}
                  className="h-6 w-6 flex items-center justify-center rounded text-fg-muted hover:text-red-500 hover:bg-red-500/10 transition-colors"
                  title={t('style.delete') || 'Delete'}
                >
                  <Icon icon="trash" size={12} />
                </button>
              </div>
            )}
          </div>
        </div>
      )})}
    </div>
    </div>
  )
}

function OcrResultsPanel() {
  const { t } = useTranslation()
  const [isProcessing, setIsProcessing] = useState(OcrService.isProcessing)
  const [progress, setProgress] = useState(OcrService.progress)
  const [history, setHistory] = useState<typeof OcrService.history>(OcrService.history)
  const [showForm, setShowForm] = useState(OcrService.showPageOcrForm)

  useEffect(() => {
    return OcrService.subscribe(() => {
      setIsProcessing(OcrService.isProcessing)
      setProgress(OcrService.progress)
      setHistory([...OcrService.history])
      setShowForm(OcrService.showPageOcrForm)
    })
  }, [])

  const [copiedId, setCopiedId] = useState<number | null>(null)

  const handleCopyEntry = useCallback((text: string, id: number) => {
    navigator.clipboard.writeText(text).catch(() => {})
    setCopiedId(id)
    setTimeout(() => setCopiedId(null), 2000)
  }, [])

  const handleClearHistory = useCallback(() => {
    OcrService.clearHistory()
  }, [])

  const handleDownload = useCallback(() => {
    const lines: string[] = []
    const docName = OcrService.documentName || 'document'
    lines.push(`OCR Results for ${docName}`)
    lines.push('='.repeat(50))
    lines.push('')

    let currentPage = -1
    let currentDoc = ''
    for (const entry of history) {
      if (entry.documentName && entry.documentName !== currentDoc) {
        currentDoc = entry.documentName
        lines.push(`=== ${currentDoc} ===`)
      }
      if (entry.pageIndex !== currentPage) {
        currentPage = entry.pageIndex
        lines.push(`--- Page ${currentPage + 1} ---`)
      }
      lines.push(entry.text)
      lines.push('')
    }

    const now = new Date()
    const ts = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')}_${String(now.getHours()).padStart(2,'0')}${String(now.getMinutes()).padStart(2,'0')}${String(now.getSeconds()).padStart(2,'0')}`
    const blob = new Blob([lines.join('\n')], { type: 'text/plain;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `ocr_${ts}.txt`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    setTimeout(() => URL.revokeObjectURL(url), 1000)
  }, [history])

  const handleFormCancel = useCallback(() => {
    OcrService.closePageOcrForm()
  }, [])

  const [formScope, setFormScope] = useState<'current' | 'range' | 'all'>('current')
  const [formFrom, setFormFrom] = useState(0)
  const [formTo, setFormTo] = useState(0)

  useEffect(() => {
    if (showForm) {
      const cp = OcrService.pageOcrCurrentPage
      setFormScope('current')
      setFormFrom(cp)
      setFormTo(Math.min(cp + 1, Math.max(0, OcrService.pageOcrTotalPages - 1)))
    }
  }, [showForm])

  const handleFormProcess = () => {
    OcrService.submitPageOcrOptions({
      scope: formScope,
      fromPage: formFrom,
      toPage: formScope === 'range' ? formTo : formFrom,
    })
  }

  const totalPages = OcrService.pageOcrTotalPages

  const stageLabels: Record<string, string> = {
    'loading-models': t('style.ocrLoadingModels') || 'Loading OCR models...',
    detecting: t('style.ocrDetecting') || 'Detecting text lines...',
    recognizing: t('style.ocrRecognizing') || 'Recognizing text...',
    complete: 'Complete',
    error: 'Error',
  }

  const linePct =
    progress && progress.totalLines > 0
      ? Math.round((progress.processedLines / progress.totalLines) * 100)
      : 0

  const hasPageProgress = progress?.currentPage != null && progress?.totalPages != null

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between px-3 py-2 border-b border-border-default bg-bg-app">
        <span className="text-xs text-fg-muted">
          {history.length > 0
            ? `${history.length} ${history.length === 1 ? (t('style.ocrScan') || 'scan') : (t('style.ocrScans') || 'scans')}`
            : ''}
        </span>
        {history.length > 0 && (
          <div className="flex items-center gap-1">
            <button
              onClick={handleDownload}
              className="flex items-center gap-1 h-7 px-2 rounded text-xs text-fg-muted hover:text-fg-primary hover:bg-bg-surface-alt transition-colors"
              title={t('style.ocrDownload') || 'Download all as text file'}
            >
              <Icon icon="download" size={12} />
              {t('style.ocrDownload') || 'Download'}
            </button>
            <button
              onClick={handleClearHistory}
              className="flex items-center gap-1 h-7 px-2 rounded text-xs text-fg-muted hover:text-fg-primary hover:bg-bg-surface-alt transition-colors"
              title={t('style.ocrClearHistory') || 'Clear history'}
            >
              <Icon icon="trash" size={12} />
              {t('style.ocrClearHistory') || 'Clear'}
            </button>
          </div>
        )}
      </div>

      {isProcessing && (
        <div className="mx-3 mt-2 mb-1 px-3 py-2.5 border border-accent/30 bg-accent/[0.06] rounded-lg">
          <div className="flex items-center gap-2 mb-1.5">
            {progress?.stage !== 'complete' && progress?.stage !== 'error' && (
              <div className="w-4 h-4 border-2 border-accent border-t-transparent rounded-full animate-spin flex-shrink-0" />
            )}
            <span className="text-xs text-fg-primary font-medium">
              {stageLabels[progress?.stage || ''] || progress?.stage || (t('style.ocrProcessing') || 'Processing...')}
            </span>
            {hasPageProgress && (
              <span className="text-xs text-fg-muted ml-auto">
                {t('style.ocrFromPage') || 'page'} {progress!.currentPage! + 1} {t('style.ocrModalOf') || 'of'} {progress!.totalPages! + 1}
              </span>
            )}
          </div>
          {progress && progress.totalLines > 0 ? (
            <div className="flex items-center gap-2">
              <div className="flex-1 bg-bg-input rounded-full h-2 overflow-hidden">
                <div
                  className="h-full bg-accent rounded-full transition-all duration-300"
                  style={{ width: `${Math.min(100, linePct)}%` }}
                />
              </div>
              <span className="text-[10px] text-fg-muted flex-shrink-0">{linePct}%</span>
            </div>
          ) : (
            <div className="flex-1 bg-bg-input rounded-full h-2 overflow-hidden">
              <div className="h-full w-1/3 bg-accent/60 rounded-full animate-indeterminate" />
            </div>
          )}
          {progress?.stage === 'recognizing' && progress?.currentLineText && (
            <p className="mt-1 text-[10px] text-fg-muted truncate">
              {progress.currentLineText}
            </p>
          )}
        </div>
      )}

      <div className="flex-1 overflow-y-auto p-3 space-y-3">
        {showForm && (
          <div className="space-y-3">
            <p className="text-xs text-fg-muted">
              {t('style.ocrModalDescription') || 'Processing may take a moment depending on page content complexity. Select the scope below.'}
            </p>

            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="radio"
                name="ocrScope"
                checked={formScope === 'current'}
                onChange={() => setFormScope('current')}
                className="mt-0.5 accent-accent"
              />
              <div>
                <span className="text-sm font-medium text-fg-primary">
                  {t('style.ocrModalCurrentPage') || 'Process this page'}
                </span>
                <span className="text-xs text-fg-muted ml-2">({t('style.ocrFromPage') || 'page'} {OcrService.pageOcrCurrentPage + 1})</span>
              </div>
            </label>

            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="radio"
                name="ocrScope"
                checked={formScope === 'range'}
                onChange={() => setFormScope('range')}
                className="mt-0.5 accent-accent"
              />
              <div className="flex flex-col gap-2">
                <span className="text-sm font-medium text-fg-primary">
                  {t('style.ocrModalRange') || 'Process page range'}
                </span>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs text-fg-muted">{t('style.ocrModalFrom') || 'from'}</span>
                  <input
                    type="number"
                    min={0}
                    max={totalPages - 1}
                    value={formFrom + 1}
                    onChange={(e) => {
                      const v = Number(e.target.value)
                      if (v >= 1 && v <= totalPages) setFormFrom(v - 1)
                    }}
                    disabled={formScope !== 'range'}
                    className="w-16 h-8 bg-bg-input border border-border-default rounded-lg text-sm text-fg-primary text-center outline-none focus:border-accent disabled:opacity-40"
                  />
                  <span className="text-xs text-fg-muted">{t('style.ocrModalTo') || 'to'}</span>
                  <input
                    type="number"
                    min={0}
                    max={totalPages - 1}
                    value={formTo + 1}
                    onChange={(e) => {
                      const v = Number(e.target.value)
                      if (v >= 1 && v <= totalPages) setFormTo(v - 1)
                    }}
                    disabled={formScope !== 'range'}
                    className="w-16 h-8 bg-bg-input border border-border-default rounded-lg text-sm text-fg-primary text-center outline-none focus:border-accent disabled:opacity-40"
                  />
                  <span className="text-xs text-fg-muted">{t('style.ocrModalOf') || 'of'} {totalPages}</span>
                </div>
              </div>
            </label>

            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="radio"
                name="ocrScope"
                checked={formScope === 'all'}
                onChange={() => setFormScope('all')}
                className="mt-0.5 accent-accent"
              />
              <div>
                <span className="text-sm font-medium text-fg-primary">
                  {t('style.ocrModalAll') || 'Process whole document'}
                </span>
                <span className="text-xs text-fg-muted ml-2">({totalPages} {(t('style.ocrModalPages') || 'pages')})</span>
              </div>
            </label>

            <div className="flex justify-end gap-2 pt-2">
              <button
                onClick={handleFormCancel}
                className="h-9 px-4 rounded-lg text-sm font-medium text-fg-secondary hover:text-fg-primary hover:bg-bg-elevated transition-colors border border-border-default"
              >
                {t('style.cancel') || 'Cancel'}
              </button>
              <button
                onClick={handleFormProcess}
                className="h-9 px-4 rounded-lg text-sm font-medium text-white bg-accent hover:bg-accent/90 transition-colors"
              >
                {t('style.ocrModalProcess') || 'Process'}
              </button>
            </div>
          </div>
        )}

        {!showForm && !isProcessing && history.length === 0 && (
          <div className="py-12 flex flex-col items-center justify-center gap-4 text-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-3xl bg-bg-app text-fg-muted">
              <Icon icon="scanText" size={24} />
            </div>
            <div className="max-w-[220px]">
              <p className="text-fg-primary font-semibold mb-2">{t('style.ocrReady') || 'OCR Workspace Ready'}</p>
              <p className="text-fg-muted text-sm">{t('style.ocrReadyHint') || 'Click the OCR or Area OCR button in the Research toolbar to extract text.'}</p>
            </div>
          </div>
        )}

        {history.map((entry) => (
          <div
            key={entry.id}
            className="rounded-lg border border-border-default bg-bg-elevated overflow-hidden"
          >
            <div className="flex items-center justify-between px-3 py-2 bg-bg-app/50 border-b border-border-default">
              <div className="flex items-center gap-2 min-w-0">
                {entry.documentName && (
                  <span className="text-[10px] text-fg-muted truncate max-w-[120px]" title={entry.documentName}>
                    {entry.documentName.replace(/\.[^.]+$/, '').slice(0, 20)}
                  </span>
                )}
                <span className="px-1.5 py-0.5 rounded text-[10px] bg-accent/10 text-accent font-semibold">
                  p.{entry.pageIndex + 1}
                </span>
              </div>
              <button
                onClick={() => handleCopyEntry(entry.text, entry.id)}
                className="flex items-center gap-1 h-6 px-2 rounded text-[10px] text-fg-muted hover:text-fg-primary hover:bg-bg-surface-alt transition-colors shrink-0"
                title={t('style.ocrCopyResult') || 'Copy this result'}
              >
                <Icon icon={copiedId === entry.id ? 'check' : 'copy'} size={10} />
                {copiedId === entry.id ? (t('style.ocrCopied') || 'Copied!') : (t('style.ocrCopyResult') || 'Copy')}
              </button>
            </div>
            <div className="p-3">
              <p className="text-xs text-fg-primary leading-relaxed whitespace-pre-wrap break-words">
                {entry.text}
              </p>
            </div>
            <div className="flex items-center gap-2 px-3 py-1.5 border-t border-border-default/50">
              <span className="text-[10px] text-fg-muted">
                {entry.lineCount} {entry.lineCount === 1 ? (t('style.ocrLine') || 'line') : (t('style.ocrLines') || 'lines')}
              </span>
              <span className="text-[10px] text-fg-muted">·</span>
              <span className="text-[10px] text-fg-muted">
                {new Date(entry.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
              </span>
              <span className="text-[10px] text-fg-muted">·</span>
              <span className="text-[10px] text-fg-muted">
                {(entry.processingTimeMs ?? 0) >= 1000
                  ? `${((entry.processingTimeMs ?? 0) / 1000).toFixed(1)}s`
                  : `${entry.processingTimeMs ?? 0}ms`}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function DictionaryPanel() {
  const { t } = useTranslation()
  const [search, setSearch] = useState('')
  const [results, setResults] = useState<{ label: string; data: any }[]>([])
  const [loading, setLoading] = useState(false)
  const [isDictLoaded, setIsDictLoaded] = useState(DictionaryService.isLoaded)
  const inputRef = useRef<HTMLInputElement>(null)
  const pendingSearch = useRef('')

  const doSearch = useCallback(async (query: string) => {
    const trimmed = query.trim()
    if (!trimmed || !DictionaryService.isLoaded) return
    setLoading(true)
    const isKhmer = /[\u1780-\u17FF]/.test(trimmed)
    const all: { label: string; data: any }[] = []
    if (isKhmer) {
      const khKh = DictionaryService.lookupKhmerKhmer(trimmed)
      if (khKh) all.push({ label: t('dashboard.dictionaryKhKh'), data: khKh })
      const khEn = DictionaryService.lookupKhmer(trimmed)
      if (khEn) all.push({ label: t('dashboard.dictionaryKhEn'), data: khEn })
    } else {
      const enKh = await DictionaryService.lookupEnglish(trimmed)
      if (enKh) all.push({ label: t('dashboard.dictionaryEnKh'), data: enKh })
      const enEn = await DictionaryService.lookupEnglishApi(trimmed)
      if (enEn) all.push({ label: t('dashboard.dictionaryEnEn'), data: enEn })
    }
    setResults(all)
    setLoading(false)
  }, [t])

  useEffect(() => {
    const unsub = DictionaryService.subscribe(() => setIsDictLoaded(DictionaryService.isLoaded))
    DictionaryService.load()
    DictionaryService.loadKhmerDict().catch(() => {})
    return unsub
  }, [])

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail
      const word = detail?.word || (window as any).__DICT_WORD__
      if (word) {
        delete (window as any).__DICT_WORD__
        setSearch(word)
        if (DictionaryService.isLoaded) {
          doSearch(word)
        } else {
          pendingSearch.current = word
        }
      }
      setTimeout(() => inputRef.current?.focus(), 100)
    }
    window.addEventListener('opakor-open-dictionary', handler)
    return () => window.removeEventListener('opakor-open-dictionary', handler)
  }, [doSearch])

  useEffect(() => {
    const word = (window as any).__DICT_WORD__
    if (word) {
      delete (window as any).__DICT_WORD__
      setSearch(word)
      if (DictionaryService.isLoaded) {
        doSearch(word)
      } else {
        pendingSearch.current = word
      }
    }
  }, [doSearch])

  useEffect(() => {
    if (isDictLoaded && pendingSearch.current) {
      doSearch(pendingSearch.current)
      pendingSearch.current = ''
    }
  }, [isDictLoaded, doSearch])

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') doSearch(search)
  }, [search, doSearch])

  if (!isDictLoaded) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-3 p-8">
        <div className="w-8 h-8 border-2 border-fg-muted/20 border-t-accent rounded-full animate-spin" />
        <p className="text-sm text-fg-muted">{t('dashboard.dictionaryLoading')}</p>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full p-4 gap-4">
      <div className="flex items-center gap-2">
        <input
          ref={inputRef}
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={t('dashboard.dictionarySearchPlaceholder')}
          className="flex-1 h-9 rounded-lg bg-bg-surface-alt border border-border-default px-3 text-sm text-fg-primary outline-none focus:border-accent placeholder:text-fg-muted/50"
        />
        <button
          onClick={() => doSearch(search)}
          disabled={loading}
          className="h-9 px-4 bg-accent text-white rounded-lg text-sm font-medium hover:bg-accent/90 transition-colors disabled:opacity-50"
        >
          {loading ? '...' : t('dashboard.dictionaryLookup')}
        </button>
      </div>

      <div className="flex-1 overflow-y-auto">
        {results.length > 0 ? (
          <div className="flex flex-col gap-3">
            {results.map((r, i) => (
              <div key={i} className="bg-bg-surface-alt rounded-xl border border-border-default overflow-hidden">
                <div className="px-4 py-2 bg-accent/10 border-b border-border-default/50">
                  <span className="text-xs font-semibold text-accent">{r.label}</span>
                </div>
                <div className="p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <h3 className="text-lg font-semibold text-fg-primary">{r.data.word}</h3>
                    {r.data.pronunciation && (
                      <span className="text-xs text-fg-muted font-mono">{r.data.pronunciation}</span>
                    )}
                  </div>
                  {r.data.pos.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-3">
                      {r.data.pos.map((p: string) => (
                        <span key={p} className="px-2 py-0.5 rounded-md bg-accent/10 text-accent text-xs font-medium">{p}</span>
                      ))}
                    </div>
                  )}
                  <ol className="space-y-2 list-decimal list-inside">
                    {r.data.definitions.map((d: string, j: number) => (
                      <li key={j} className="text-sm text-fg-primary leading-relaxed">{d}</li>
                    ))}
                  </ol>
                  {r.data.forms?.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-border-default">
                      <span className="text-xs text-fg-muted">{r.data.source === 'khmer' ? 'Examples: ' : 'Forms: '}</span>
                      <span className="text-xs text-fg-secondary">{r.data.forms.join('; ')}</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center h-full gap-2 text-center p-4">
            <p className="text-sm text-fg-muted">{t('dashboard.dictionaryNoResult').replace('{word}', search || '""')}</p>
            <p className="text-xs text-fg-muted/70">{t('dashboard.dictionaryNoResultHint')}</p>
          </div>
        )}
      </div>
    </div>
  )
}

export const RightPanel = ({ isOpen, onClose, documentId, mode }: RightPanelProps) => {
  const { t } = useTranslation()
  const isCompact = useResponsive()

  const isSearch = mode === 'search'
  const isOcr = mode === 'ocrResults'
  const isDictionary = mode === 'dictionary'

  const panelClasses = isCompact
    ? `fixed bottom-0 left-0 right-0 h-[50vh] bg-bg-surface border-t border-border-default flex flex-col overflow-hidden z-20 transition-transform duration-300 ${isOpen ? 'translate-y-0' : 'translate-y-full'}`
    : `w-[340px] bg-bg-surface border-l border-border-default flex flex-col overflow-hidden relative z-10 transition-all duration-300 ${isOpen ? 'animate-in slide-in-from-right' : 'hidden'}`

  return (
    <div className={panelClasses}>
      <div className="flex items-center justify-between px-4 py-3 border-b border-border-default bg-bg-app">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-bg-surface-alt text-accent">
            <Icon icon={isSearch ? 'search' : isOcr ? 'scanText' : isDictionary ? 'book' : 'comment'} size={18} />
          </div>
          <div>
            <h2 className="text-sm font-semibold text-fg-primary">
              {isSearch ? (t('style.searchTitle') || 'Search') : isOcr ? (t('style.ocrTitle') || 'Text Recognition') : isDictionary ? (t('dashboard.dictionary') || 'Dictionary') : (t('style.annotationChat') || 'Annotation chat')}
            </h2>
          </div>
        </div>

        <button
          type="button"
          onClick={onClose}
          className="rounded-full p-2 text-fg-secondary hover:text-fg-primary"
          aria-label="Close panel"
        >
          <Icon icon="x" size={18} />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto">
        {isSearch ? <SearchPanel documentId={documentId} isVisible={isOpen} /> : isOcr ? <OcrResultsPanel /> : isDictionary ? <DictionaryPanel /> : <CommentsPanel documentId={documentId} />}
      </div>
    </div>
  )
}
